import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

type PublicHospital = {
  id: string;
  name: string;
  address?: string | null;
  group?: { id: string; name: string } | null;
};

export default function PartnerRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    partnerType: 'DOCTOR',
    termsAccepted: false,
  });
  const [selectedHospitalIds, setSelectedHospitalIds] = useState<string[]>([]);
  const [hospitalFilter, setHospitalFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: hospitals = [], isLoading: hospitalsLoading } = useQuery({
    queryKey: ['partner-public-hospitals'],
    queryFn: async () => {
      const res = await referralPartnerApi.publicHospitals();
      return (res.data?.data ?? []) as PublicHospital[];
    },
  });

  const filteredHospitals = useMemo(() => {
    const q = hospitalFilter.trim().toLowerCase();
    if (!q) return hospitals;
    return hospitals.filter((h) => {
      const chain = h.group?.name?.toLowerCase() ?? '';
      return h.name.toLowerCase().includes(q) || chain.includes(q);
    });
  }, [hospitals, hospitalFilter]);

  const toggleHospital = (id: string, checked: boolean) => {
    setSelectedHospitalIds((prev) =>
      checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.termsAccepted) {
      toast.error('Please accept terms and conditions');
      return;
    }
    if (selectedHospitalIds.length === 0) {
      toast.error('Select at least one hospital');
      return;
    }
    setLoading(true);
    try {
      await referralPartnerApi.register({
        ...form,
        termsAccepted: true,
        hospitalIds: selectedHospitalIds,
      });
      toast.success('Registration submitted. You will be notified when approved.');
      navigate('/partner/login');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0f1a] px-4 py-12">
      <div className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900/80 p-5 sm:p-8 shadow-2xl backdrop-blur-xl">
        <h1 className="text-2xl font-bold text-white">Partner registration</h1>
        <p className="mt-1 text-sm text-slate-400">Join our Fast Growing Network</p>
        <form onSubmit={submit} className="mt-6 grid gap-3 sm:grid-cols-2">
          <Input
            placeholder="First name"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            className="border-white/10 bg-white/5 text-white sm:col-span-1 h-11"
            required
          />
          <Input
            placeholder="Last name"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            className="border-white/10 bg-white/5 text-white h-11"
            required
          />
          <Input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border-white/10 bg-white/5 text-white sm:col-span-2 h-11"
            required
          />
          <Input
            placeholder="10-digit mobile"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="border-white/10 bg-white/5 text-white sm:col-span-2 h-11"
            maxLength={10}
            required
          />
          <div className="sm:col-span-2 space-y-2">
            <p className="text-sm font-medium text-slate-200">Hospitals you refer to</p>
            <p className="text-xs text-slate-400">
              Select one or more chain hospitals. A sub-admin or chain admin for any selected site can
              approve your account.
            </p>
            <Input
              placeholder="Search hospitals…"
              value={hospitalFilter}
              onChange={(e) => setHospitalFilter(e.target.value)}
              className="border-white/10 bg-white/5 text-white h-10"
            />
            <div className="max-h-44 overflow-y-auto rounded-lg border border-white/10 bg-white/5 p-2 space-y-1">
              {hospitalsLoading ? (
                <p className="text-sm text-slate-400 px-2 py-3">Loading hospitals…</p>
              ) : filteredHospitals.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3">No hospitals match your search.</p>
              ) : (
                filteredHospitals.map((h) => (
                  <label
                    key={h.id}
                    className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 hover:bg-white/5"
                  >
                    <Checkbox
                      checked={selectedHospitalIds.includes(h.id)}
                      onCheckedChange={(v) => toggleHospital(h.id, v === true)}
                      className="mt-0.5 border-white/30 data-[state=checked]:bg-cyan-600"
                    />
                    <span className="text-sm text-slate-200">
                      {h.name}
                      {h.group?.name ? (
                        <span className="block text-xs text-slate-500">{h.group.name}</span>
                      ) : null}
                    </span>
                  </label>
                ))
              )}
            </div>
            {selectedHospitalIds.length > 0 && (
              <p className="text-xs text-cyan-400/90">{selectedHospitalIds.length} selected</p>
            )}
          </div>
          <Input
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="border-white/10 bg-white/5 text-white sm:col-span-2 h-11"
            required
          />
          <label className="flex items-start gap-2 text-sm text-slate-300 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.termsAccepted}
              onChange={(e) => setForm({ ...form, termsAccepted: e.target.checked })}
              className="mt-1"
            />
            I accept the terms and confirm I will obtain patient consent before sharing details.
          </label>
          <Button
            type="submit"
            disabled={loading}
            className="sm:col-span-2 bg-gradient-to-r from-cyan-500 to-violet-600 h-11"
          >
            {loading ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm text-slate-400">
          <Link to="/partner/login" className="text-cyan-400 hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
